import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import DialogContentText from '@mui/material/DialogContentText';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import LibraryAddCheckTwoToneIcon from '@mui/icons-material/LibraryAddCheckTwoTone';
import { DynamicValue } from '@fulcrumapp/fulcrum-core';
import CustomConfirmDialog from '../../shared/material_components/custom-confirm-dialog';
import EditChecklistDialog from './edit-checklist-dialog';
import FillOutChecklistDialog from './fill-out-checklist-dialog';
import { getChecklistCompletionText } from './utils';
import Button from '../../components/button';

interface Props {
  hidden?: boolean;
  value: DynamicValue;
  onChange: (value: DynamicValue) => void;
}

const ChecklistButton = styled(Button)({
  flexGrow: 1,
  justifyContent: 'space-between',
  fontWeight: 'normal',
  textTransform: 'none',
  color: 'rgba(0, 0, 0, 0.54)',
});

const ChecklistField = ({ hidden = false, value, onChange }: Props) => {
  const [editChecklistDialogOpen, setEditChecklistDialogOpen] = useState(false);
  const [fillOutChecklistDialogOpen, setFillOutChecklistDialogOpen] = useState(false);
  const [deleteChecklistDialogOpen, setDeleteChecklistDialogOpen] = useState(false);

  const handleEditChecklist = () => {
    setEditChecklistDialogOpen(true);
  };

  const handleCloseEditChecklistDialog = () => {
    setEditChecklistDialogOpen(false);
  };

  const handleFillOutChecklist = () => {
    setFillOutChecklistDialogOpen(true);
  };

  const handleCloseFillOutChecklistDialog = () => {
    setFillOutChecklistDialogOpen(false);
  };

  const handleDeleteChecklist = () => {
    setDeleteChecklistDialogOpen(true);
  };

  const handleCloseDeleteChecklistDialog = () => {
    setDeleteChecklistDialogOpen(false);
  };

  const handleConfirmDeleteChecklist = () => {
    onChange(new DynamicValue(value.element, []));
    setDeleteChecklistDialogOpen(false);
  };

  const renderAddChecklistButton = () => (
    <>
      <Button
        size="small"
        onClick={handleEditChecklist}
        startIcon={<LibraryAddCheckTwoToneIcon />}
      >
        Add Checklist
      </Button>
    </>
  );

  const checklistCompletionText = `Checklist (${getChecklistCompletionText(value)})`;

  const renderChecklistField = () => (
    <Box display="flex" flexDirection="row">
      <ChecklistButton
        variant="outlined"
        fullWidth
        endIcon={<ArrowRightIcon />}
        onClick={handleFillOutChecklist}
      >
        {checklistCompletionText}
      </ChecklistButton>

      <IconButton
        aria-label="edit checklist"
        sx={{ alignSelf: 'center' }}
        onClick={handleEditChecklist}
        size="large"
      >
        <EditTwoToneIcon />
      </IconButton>

      <IconButton
        aria-label="delete checklist"
        sx={{ alignSelf: 'center' }}
        onClick={handleDeleteChecklist}
        size="large"
      >
        <DeleteTwoToneIcon />
      </IconButton>
    </Box>
  );

  return (
    <>
      {!hidden
        && (
          <>
            {value.isEmpty ? renderAddChecklistButton() : renderChecklistField()}

            <EditChecklistDialog
              open={editChecklistDialogOpen}
              onClose={handleCloseEditChecklistDialog}
              value={value}
              onChange={onChange}
              isCreating={value.isEmpty}
            />

            <FillOutChecklistDialog
              open={fillOutChecklistDialogOpen}
              onClose={handleCloseFillOutChecklistDialog}
              value={value}
              onChange={onChange}
            />

            <CustomConfirmDialog
              open={deleteChecklistDialogOpen}
              onClose={handleCloseDeleteChecklistDialog}
              title="Delete Checklist?"
              positive="delete"
              onNegative={handleCloseDeleteChecklistDialog}
              onPositive={handleConfirmDeleteChecklist}
            >
              <DialogContentText>
                Are you sure you want to delete this checklist?
              </DialogContentText>
            </CustomConfirmDialog>
          </>
        )}
    </>
  );
};

export default ChecklistField;
